/**
 * The convo-summary endpoint returns a free-text `currentOutcome` (e.g. "Interested - ready to apply"
 * or "Disqualified - co-signer required"). These helpers surface its short leading clause as a status
 * label and pick a badge colour. Extracted so the dashboard "Convo summary" panel and the conversation
 * sidebar derive the same status from the same outcome string.
 */

/** Leading clause of `currentOutcome`, or `null` when it is empty / too long to be a status pill. */
export function convoOutcomeLabel(currentOutcome: string | null | undefined): string | null {
  const outcome = currentOutcome?.trim();
  if (!outcome) {
    return null;
  }

  // Split only on a spaced dash/en-dash or a colon — NOT on bare hyphens — so hyphenated
  // words ("pre-screening", "co-signer", "follow-up") stay intact instead of being chopped
  // into a misleading fragment like "Renter ghosted after pre".
  const first = outcome.split(/\s+[-–]\s+|:\s*/)[0].trim();
  return first.length > 0 && first.length <= 24 ? first : null;
}

/** Badge colour for an outcome — green for positive/engaged outcomes, grey otherwise. */
export function convoOutcomeVariant(currentOutcome: string | null | undefined): 'green' | 'grey' {
  const label = (convoOutcomeLabel(currentOutcome) || '').toLowerCase();
  // Negative outcomes are never green — guard first, since "disqualified"/"unqualified" contain "qualif".
  if (/disqualif|unqualif/.test(label)) {
    return 'grey';
  }
  return /interest|positive|qualif|approv|appli|accept|complet/.test(label) ? 'green' : 'grey';
}
